import { useState, useEffect, useMemo, useRef } from "react";
import debounce from "lodash/debounce";
import db from "../../database";
import usePOSConfigStore from "../../stores/usePOSConfigStore";

/**
 * Search, filter, category/brand, sort and pagination logic for the ProductsPopup.
 *
 * @param {Object} params
 * @param {Array}  params.productData         - Full product list passed to the popup
 * @param {Array}  params.selectedItems        - Currently selected cart items (used in dependency array)
 * @param {boolean} params.visibleProductsModal - Whether the products modal is open
 */
const useProductPopupSearch = ({ productData, selectedItems, visibleProductsModal }) => {
  const posConfig = usePOSConfigStore((s) => s.posConfig);
  const itemsPerPage = 10;

  const [searchQuery, setSearchQuery] = useState("");
  const [rawSearchQuery, setRawSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [sortOrder, setSortOrder] = useState("default");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [categoriesData, setCategoriesData] = useState([]);
  const [brandsData, setBrandsData] = useState([]);

  // Debounced search: rawSearchQuery updates instantly for display, searchQuery updates after 300ms for filtering
  const debouncedSetSearch = useRef(
    debounce((val) => {
      setSearchQuery(val);
      setCurrentPage(0);
    }, 300)
  ).current;

  useEffect(() => {
    return () => debouncedSetSearch.cancel();
  }, []);

  // Load categories and brands when modal opens
  useEffect(() => {
    if (visibleProductsModal) {
      const fetchFilters = async () => {
        try {
          const cats = await db.productCategories.toArray();
          const mappedCats = cats.map((item) => ({
            ...item,
            label: item.value,
            value: item.mProductCategoryId,
          }));
          setCategoriesData(mappedCats.sort((a, b) => a.label.localeCompare(b.label)));
        } catch (e) {
          console.error("Error loading categories:", e);
        }
        try {
          const brands = await db.productBrands.toArray();
          const mappedBrands = brands.map((item) => ({
            ...item,
            label: item.name,
            value: item.brandId,
          }));
          setBrandsData(mappedBrands.sort((a, b) => a.label.localeCompare(b.label)));
        } catch (e) {
          console.error("Error loading brands:", e);
        }
      };
      fetchFilters();
    }
  }, [visibleProductsModal]);

  // Filter + sort products
  useEffect(() => {
    let cancelled = false;
    const lowerQuery = searchQuery.toLowerCase();

    const filterAndSort = (source) => {
      let filtered = source.filter((item) => {
        const isValidSegment = item.productSegment !== "CB";

        // Category filter
        if (selectedCategory && item.mProductCategoryId !== selectedCategory) return false;

        // Brand filter
        if (selectedBrand && item.brandId !== selectedBrand) return false;

        if (!lowerQuery) return isValidSegment;

        // Expanded search fields: name, value (SKU), upc, description, batchIndex, upcIndex
        const matchesSearch =
          item.name?.toLowerCase().includes(lowerQuery) || item.value?.toLowerCase().includes(lowerQuery) || (item.upc && item.upc.toLowerCase().includes(lowerQuery));
        // ||
        // (item.description && item.description.toLowerCase().includes(lowerQuery)) ||
        // (Array.isArray(item.batchIndex) && item.batchIndex.some((b) => b?.toLowerCase().includes(lowerQuery))) ||
        // (Array.isArray(item.upcIndex) && item.upcIndex.some((u) => u?.toLowerCase().includes(lowerQuery)));

        return isValidSegment && matchesSearch;
      });

      // Smart search: exact SKU match gets priority
      if (posConfig?.is_smart_product_search === "Y" && lowerQuery) {
        const exactMatch = filtered.filter((item) => item.value === searchQuery);
        const rest = filtered.filter((item) => item.value !== searchQuery);
        filtered = [...exactMatch, ...rest];
      }

      // Sort
      if (sortOrder === "price_asc") {
        filtered.sort((a, b) => (a.sunitprice || 0) - (b.sunitprice || 0));
      } else if (sortOrder === "price_desc") {
        filtered.sort((a, b) => (b.sunitprice || 0) - (a.sunitprice || 0));
      } else if (sortOrder === "name_asc") {
        filtered.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      } else if (sortOrder === "name_desc") {
        filtered.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
      }

      return filtered;
    };

    let result = filterAndSort(productData);

    if (result.length === 0 && lowerQuery) {
      setFilteredProducts([]);
      db.products
        .toArray()
        .then((allProducts) => {
          if (cancelled) return;
          const dbResult = filterAndSort(allProducts);
          if (dbResult.length > 0) {
            setFilteredProducts(dbResult);
            setCurrentPage(0);
          }
        })
        .catch(() => {});
    } else {
      setFilteredProducts(result);
      setCurrentPage(0);
    }

    return () => {
      cancelled = true;
    };
  }, [searchQuery, productData, sortOrder, selectedCategory, selectedBrand]);

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredProducts.slice(startIndex, endIndex);
  }, [filteredProducts, currentPage]);

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  const resetSearch = () => {
    setSearchQuery("");
    setRawSearchQuery("");
    setCurrentPage(0);
    setSelectedCategory(null);
    setSelectedBrand(null);
    setSortOrder("default");
  };

  return {
    searchQuery,
    rawSearchQuery,
    setRawSearchQuery,
    debouncedSetSearch,
    currentPage,
    setCurrentPage,
    filteredProducts,
    paginatedData,
    totalPages,
    sortOrder,
    setSortOrder,
    selectedCategory,
    setSelectedCategory,
    selectedBrand,
    setSelectedBrand,
    categoriesData,
    brandsData,
    handlePreviousPage,
    handleNextPage,
    resetSearch,
  };
};

export default useProductPopupSearch;
